import React, { useEffect, useRef, useState } from "react";
import AdminLayout from "../components/AdminLayout";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { CurrencyInr, FloppyDisk, Info, Buildings, ImageSquare, X, Certificate, Palette, MagicWand, EnvelopeSimple, PaperPlaneTilt, CheckCircle, Warning, ChatCircleText } from "@phosphor-icons/react";
import VisualCertificateEditor from "../components/VisualCertificateEditor";
import { applyTheme, DEFAULT_THEME } from "../lib/theme";

const TABS = [
  { id: "institute", label: "Institute Details" },
  { id: "payments", label: "Payment Methods" },
  { id: "theme", label: "Colour Theme" },
  { id: "notifications", label: "Notifications" },
  { id: "certificate", label: "Certificate Template", adminOnly: true },
];

export default function Settings() {
  const { user: me } = useAuth();
  const [tab, setTab] = useState("institute");
  const tabs = TABS.filter((t) => !t.adminOnly || me?.role === "admin");

  return (
    <AdminLayout>
      <div className="mb-8">
        <div className="font-mono-sms text-xs uppercase tracking-[0.3em] text-[var(--sms-primary)] mb-3">Settings</div>
        <h1 className="font-display text-5xl font-black tracking-tighter">Configuration.</h1>
        <p className="text-slate-500 mt-2">Branding, payment account, and operational defaults.</p>
      </div>

      <div className="flex items-center gap-1 border border-slate-200 inline-flex mb-8" data-testid="settings-tabs">
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`tab-${t.id}`}
            className={`px-5 py-2 font-mono-sms text-[10px] uppercase tracking-[0.2em] ${
              tab === t.id ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "institute" && <InstituteForm />}
      {tab === "payments" && <PaymentsTab />}
      {tab === "theme" && <ThemeForm />}
      {tab === "notifications" && <NotificationsForm />}
      {tab === "certificate" && <CertificateForm />}
    </AdminLayout>
  );
}

// ----------------------------- Certificate Template (Visual editor) -----------------------------
function CertificateForm() {
  return (
    <div className="space-y-3" data-testid="certificate-template-form">
      <div className="flex items-center gap-2">
        <Certificate size={20} weight="duotone" className="text-[var(--sms-primary)]" />
        <div className="font-mono-sms text-xs uppercase tracking-[0.2em]">Certificate template — visual editor</div>
      </div>
      <div className="text-[11px] text-slate-500 inline-flex items-center gap-1">
        <Info size={12} weight="duotone" /> Drag, resize, and edit text directly on the canvas. Upload a custom background image to replace the default gold frame.
      </div>
      <VisualCertificateEditor />
    </div>
  );
}

// ----------------------------- Institute -----------------------------
function InstituteForm() {
  const [form, setForm] = useState({ name: "", tagline: "", hero_subtitle: "", address: "", phone: "", email: "", gstin: "", logo_base64: null });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/settings/institute")
      .then((r) => setForm({
        name: r.data.name || "",
        tagline: r.data.tagline || "",
        hero_subtitle: r.data.hero_subtitle || "",
        address: r.data.address || "",
        phone: r.data.phone || "",
        email: r.data.email || "",
        gstin: r.data.gstin || "",
        logo_base64: r.data.logo_base64 || null,
      }))
      .finally(() => setLoading(false));
  }, []);

  const onLogo = (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Logo must be an image (PNG / JPG / SVG)");
      return;
    }
    if (file.size > 4 * 1024 * 1024) {
      toast.error("Logo must be under 4 MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, logo_base64: reader.result }));
    reader.readAsDataURL(file);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put("/settings/institute", form);
      setForm({
        name: data.name || "",
        tagline: data.tagline || "",
        hero_subtitle: data.hero_subtitle || "",
        address: data.address || "",
        phone: data.phone || "",
        email: data.email || "",
        gstin: data.gstin || "",
        logo_base64: data.logo_base64 || null,
      });
      toast.success("Institute details saved · they appear on every printable PDF");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className="max-w-3xl border border-slate-200 p-8" data-testid="institute-settings-form">
      <div className="flex items-center gap-2 mb-6">
        <Buildings size={20} weight="duotone" className="text-[var(--sms-primary)]" />
        <div className="font-mono-sms text-xs uppercase tracking-[0.2em]">Institute branding</div>
      </div>

      {loading ? (
        <div className="text-sm text-slate-400 font-mono-sms uppercase tracking-[0.2em]">Loading…</div>
      ) : (
        <div className="grid md:grid-cols-3 gap-6">
          {/* Logo column */}
          <div className="md:col-span-1">
            <div className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2">Logo</div>
            <div
              className="aspect-square border border-slate-300 bg-slate-50 flex items-center justify-center overflow-hidden relative"
              data-testid="institute-logo-preview"
            >
              {form.logo_base64 ? (
                <img src={form.logo_base64} alt="logo preview" className="max-w-full max-h-full object-contain" />
              ) : (
                <div className="text-center text-slate-400">
                  <ImageSquare size={36} weight="duotone" />
                  <div className="font-mono-sms text-[10px] uppercase tracking-[0.2em] mt-2">No logo</div>
                </div>
              )}
              {form.logo_base64 && (
                <button
                  type="button"
                  onClick={() => setForm({ ...form, logo_base64: null })}
                  data-testid="institute-logo-remove"
                  title="Remove logo"
                  className="absolute top-1 right-1 bg-white/90 border border-slate-300 p-1 hover:bg-red-50 hover:border-red-300"
                >
                  <X size={12} weight="bold" />
                </button>
              )}
            </div>
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp,image/svg+xml"
              ref={fileRef}
              onChange={(e) => onLogo(e.target.files?.[0])}
              data-testid="institute-logo-input"
              className="hidden"
            />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              data-testid="institute-logo-upload"
              className="sms-btn-secondary w-full mt-3 text-xs"
            >
              {form.logo_base64 ? "Replace logo" : "Upload logo"}
            </button>
            <div className="text-[11px] text-slate-400 mt-2">PNG / JPG / WebP / SVG · up to 4 MB</div>
          </div>

          {/* Fields column */}
          <div className="md:col-span-2 space-y-5">
            <Field label="Institute name" required testid="input-institute-name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} placeholder="Acme Polytechnic" />
            <Field label="Tagline (shown on the public landing page hero)" testid="input-institute-tagline" value={form.tagline} onChange={(v) => setForm({ ...form, tagline: v })} placeholder="The skill academy where careers begin." hint="Becomes the orange accent line above the headline." />
            <Field label="Hero subtitle (paragraph below the headline)" multiline testid="input-institute-hero-subtitle" value={form.hero_subtitle} onChange={(v) => setForm({ ...form, hero_subtitle: v })} placeholder="One or two sentences describing what students will learn and why they should enrol." />
            <Field label="Address" multiline testid="input-institute-address" value={form.address} onChange={(v) => setForm({ ...form, address: v })} placeholder="42 Brigade Road, Bengaluru 560001" />
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Phone number" testid="input-institute-phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} placeholder="+91 80 1234 5678" />
              <Field label="Email" testid="input-institute-email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} placeholder="office@acme.edu" />
            </div>
            <Field label="GSTIN" testid="input-institute-gstin" value={form.gstin} onChange={(v) => setForm({ ...form, gstin: v.toUpperCase() })} placeholder="29ABCDE1234F2Z5" mono />
          </div>
        </div>
      )}

      {!loading && (
        <div className="flex items-center gap-3 pt-6 mt-6 border-t border-slate-200">
          <button type="submit" disabled={saving} data-testid="institute-save" className="sms-btn-primary inline-flex items-center gap-2">
            <FloppyDisk size={14} weight="bold" />
            {saving ? "Saving…" : "Save institute details"}
          </button>
          <div className="text-[11px] text-slate-500 inline-flex items-center gap-1">
            <Info size={12} weight="duotone" /> Saved details appear on the header of every printable PDF.
          </div>
        </div>
      )}
    </form>
  );
}

// ----------------------------- UPI -----------------------------
// ----------------------------- Payment Methods (UPI / Bank / Cash) -----------------------------
function PaymentsTab() {
  const [sub, setSub] = useState("upi");
  const subTabs = [
    { id: "upi", label: "UPI" },
    { id: "bank", label: "Bank Transfer" },
    { id: "cash", label: "Cash" },
  ];
  return (
    <div className="space-y-6" data-testid="payments-tab">
      <div className="flex items-center gap-1 border border-slate-200 inline-flex" data-testid="payments-subtabs">
        {subTabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setSub(t.id)}
            data-testid={`subtab-${t.id}`}
            className={`px-4 py-2 font-mono-sms text-[10px] uppercase tracking-[0.2em] ${
              sub === t.id ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>
      {sub === "upi" && <UpiForm />}
      {sub === "bank" && <BankForm />}
      {sub === "cash" && <CashForm />}
    </div>
  );
}

function BankForm() {
  const [form, setForm] = useState({ account_name: "", account_number: "", ifsc: "", bank_name: "", branch: "", instructions: "", enabled: false });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings/bank")
      .then((r) => setForm({
        account_name: r.data.account_name || "",
        account_number: r.data.account_number || "",
        ifsc: r.data.ifsc || "",
        bank_name: r.data.bank_name || "",
        branch: r.data.branch || "",
        instructions: r.data.instructions || "",
        enabled: !!r.data.enabled,
      }))
      .finally(() => setLoading(false));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put("/settings/bank", form);
      setForm({
        account_name: data.account_name || "",
        account_number: data.account_number || "",
        ifsc: data.ifsc || "",
        bank_name: data.bank_name || "",
        branch: data.branch || "",
        instructions: data.instructions || "",
        enabled: !!data.enabled,
      });
      toast.success("Bank transfer settings saved");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className="max-w-2xl border border-slate-200 p-8" data-testid="bank-settings-form">
      <div className="flex items-center gap-2 mb-6">
        <Buildings size={20} weight="duotone" className="text-[var(--sms-primary)]" />
        <div className="font-mono-sms text-xs uppercase tracking-[0.2em]">Bank Account · NEFT / IMPS / RTGS</div>
      </div>

      {loading ? (
        <div className="text-sm text-slate-400 font-mono-sms uppercase tracking-[0.2em]">Loading…</div>
      ) : (
        <div className="space-y-5">
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" data-testid="bank-enabled" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} className="w-4 h-4 accent-[var(--sms-primary)]" />
            <span className="text-sm">Accept bank transfers on the public payment page</span>
          </label>
          <Field label="Account holder name" required={form.enabled} testid="bank-account-name" value={form.account_name} onChange={(v) => setForm({ ...form, account_name: v })} placeholder="As per bank records" />
          <div className="grid grid-cols-2 gap-4">
            <Field label="Account number" required={form.enabled} mono testid="bank-account-number" value={form.account_number} onChange={(v) => setForm({ ...form, account_number: v })} />
            <Field label="IFSC" required={form.enabled} mono testid="bank-ifsc" value={form.ifsc} onChange={(v) => setForm({ ...form, ifsc: v.toUpperCase() })} placeholder="HDFC0000123" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Bank name" testid="input-bank-name" value={form.bank_name} onChange={(v) => setForm({ ...form, bank_name: v })} placeholder="HDFC Bank" />
            <Field label="Branch (optional)" testid="input-bank-branch" value={form.branch} onChange={(v) => setForm({ ...form, branch: v })} />
          </div>
          <Field label="Payment instructions (optional)" multiline testid="input-bank-instructions" value={form.instructions} onChange={(v) => setForm({ ...form, instructions: v })} placeholder="Shown to the applicant on the public payment page after they select Bank Transfer" />
          <div className="flex items-center gap-3 pt-3 border-t border-slate-200">
            <button type="submit" disabled={saving} data-testid="bank-save" className="sms-btn-primary inline-flex items-center gap-2">
              <FloppyDisk size={14} weight="bold" />
              {saving ? "Saving…" : "Save settings"}
            </button>
            <div className="text-[11px] text-slate-500 inline-flex items-center gap-1">
              <Info size={12} weight="duotone" /> Applicants enter their bank transaction reference after paying — admins verify in the dashboard.
            </div>
          </div>
        </div>
      )}
    </form>
  );
}

function CashForm() {
  const [form, setForm] = useState({ instructions: "", enabled: false });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings/cash")
      .then((r) => setForm({ instructions: r.data.instructions || "", enabled: !!r.data.enabled }))
      .finally(() => setLoading(false));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put("/settings/cash", form);
      setForm({ instructions: data.instructions || "", enabled: !!data.enabled });
      toast.success("Cash payment settings saved");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className="max-w-2xl border border-slate-200 p-8" data-testid="cash-settings-form">
      <div className="flex items-center gap-2 mb-6">
        <CurrencyInr size={20} weight="duotone" className="text-[var(--sms-primary)]" />
        <div className="font-mono-sms text-xs uppercase tracking-[0.2em]">Cash · in-person at counter</div>
      </div>

      {loading ? (
        <div className="text-sm text-slate-400 font-mono-sms uppercase tracking-[0.2em]">Loading…</div>
      ) : (
        <div className="space-y-5">
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" data-testid="cash-enabled" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} className="w-4 h-4 accent-[var(--sms-primary)]" />
            <span className="text-sm">Accept cash payments at the office</span>
          </label>
          <Field label="Counter instructions" multiline testid="input-cash-instructions" value={form.instructions} onChange={(v) => setForm({ ...form, instructions: v })} placeholder="e.g. Office hours 10am–6pm. Bring your application ID. Cash receipt is issued at the desk." />
          <div className="flex items-center gap-3 pt-3 border-t border-slate-200">
            <button type="submit" disabled={saving} data-testid="cash-save" className="sms-btn-primary inline-flex items-center gap-2">
              <FloppyDisk size={14} weight="bold" />
              {saving ? "Saving…" : "Save settings"}
            </button>
            <div className="text-[11px] text-slate-500 inline-flex items-center gap-1">
              <Info size={12} weight="duotone" /> Cash payments can be verified by any logged-in staff member from the dashboard.
            </div>
          </div>
        </div>
      )}
    </form>
  );
}

// ----------------------------- UPI -----------------------------
function UpiForm() {
  const [form, setForm] = useState({ vpa: "", payee_name: "", instructions: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings/upi")
      .then((r) => setForm({ vpa: r.data.vpa || "", payee_name: r.data.payee_name || "", instructions: r.data.instructions || "" }))
      .finally(() => setLoading(false));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put("/settings/upi", form);
      setForm({ vpa: data.vpa, payee_name: data.payee_name, instructions: data.instructions || "" });
      toast.success("UPI settings saved");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className="max-w-2xl border border-slate-200 p-8" data-testid="upi-settings-form">
      <div className="flex items-center gap-2 mb-6">
        <CurrencyInr size={20} weight="duotone" className="text-[var(--sms-primary)]" />
        <div className="font-mono-sms text-xs uppercase tracking-[0.2em]">UPI Account</div>
      </div>

      {loading ? (
        <div className="text-sm text-slate-400 font-mono-sms uppercase tracking-[0.2em]">Loading…</div>
      ) : (
        <div className="space-y-5">
          <Field label="UPI VPA" required testid="input-upi-vpa" mono value={form.vpa} onChange={(v) => setForm({ ...form, vpa: v })} placeholder="name@bankhandle" hint="e.g. sms.registrar@hdfcbank" />
          <Field label="Payee name" required testid="input-upi-payee" value={form.payee_name} onChange={(v) => setForm({ ...form, payee_name: v })} placeholder="As registered with the UPI ID" />
          <Field label="Payment instructions (optional)" multiline testid="input-upi-instructions" value={form.instructions} onChange={(v) => setForm({ ...form, instructions: v })} placeholder="Shown on the payment page after the QR — e.g. 'After paying, copy the UTR from your UPI app and paste below'" />
          <div className="flex items-center gap-3 pt-3 border-t border-slate-200">
            <button type="submit" disabled={saving} data-testid="upi-save" className="sms-btn-primary inline-flex items-center gap-2">
              <FloppyDisk size={14} weight="bold" />
              {saving ? "Saving…" : "Save settings"}
            </button>
            <div className="text-[11px] text-slate-500 inline-flex items-center gap-1">
              <Info size={12} weight="duotone" /> Saved settings appear on every public payment page.
            </div>
          </div>
        </div>
      )}
    </form>
  );
}

// ----------------------------- shared field -----------------------------
function Field({ label, value, onChange, placeholder, required, multiline, type = "text", testid, mono, hint }) {
  return (
    <div>
      <div className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2">{label}{required && " *"}</div>
      {multiline ? (
        <textarea
          rows={3}
          required={required}
          className={`sms-input ${mono ? "font-mono-sms" : ""}`}
          placeholder={placeholder}
          data-testid={testid}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      ) : (
        <input
          type={type}
          required={required}
          className={`sms-input ${mono ? "font-mono-sms" : ""}`}
          placeholder={placeholder}
          data-testid={testid}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
      {hint && <div className="text-[11px] text-slate-400 mt-1">{hint}</div>}
    </div>
  );
}

// ----------------------------- Theme -----------------------------
function ThemeForm() {
  const [form, setForm] = useState({ ...DEFAULT_THEME });
  const [parentUrl, setParentUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [detecting, setDetecting] = useState(false);
  const [detectedSource, setDetectedSource] = useState(null);
  const [swatches, setSwatches] = useState([]); // [{hex, score, count, tag?}]

  useEffect(() => {
    api.get("/settings/theme")
      .then((r) => setForm({ ...DEFAULT_THEME, ...r.data }))
      .finally(() => setLoading(false));
  }, []);

  // Live preview — apply CSS variables as user picks colors
  useEffect(() => {
    if (!loading) applyTheme(form);
  }, [form, loading]);

  const pickSwatch = (hex, role = "primary") => {
    if (role === "primary") {
      const h = hex.replace("#", "");
      const r = Math.max(0, Math.round(parseInt(h.slice(0, 2), 16) * 0.82));
      const g = Math.max(0, Math.round(parseInt(h.slice(2, 4), 16) * 0.82));
      const b = Math.max(0, Math.round(parseInt(h.slice(4, 6), 16) * 0.82));
      const hover = "#" + [r, g, b].map((v) => v.toString(16).padStart(2, "0")).join("").toUpperCase();
      setForm({ ...form, primary: hex.toUpperCase(), primary_hover: hover });
    } else {
      setForm({ ...form, [role]: hex.toUpperCase() });
    }
  };

  const detect = async () => {
    if (!parentUrl.trim()) {
      toast.error("Enter a website URL first");
      return;
    }
    setDetecting(true);
    setDetectedSource(null);
    setSwatches([]);
    try {
      const { data } = await api.post("/settings/theme/detect", { url: parentUrl });
      const next = { ...form, primary: data.primary, primary_hover: data.primary_hover };
      if (data.accent) next.accent = data.accent;
      setForm(next);
      setDetectedSource(data.source || null);
      setSwatches(Array.isArray(data.swatches) ? data.swatches : []);
      const accentNote = data.accent ? ` + accent ${data.accent}` : "";
      toast.success(`Detected ${(data.swatches || []).length || 1} colour${(data.swatches || []).length === 1 ? "" : "s"}${accentNote} · click any swatch to use as primary`);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || err.message);
    } finally {
      setDetecting(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put("/settings/theme", form);
      setForm({ ...DEFAULT_THEME, ...data });
      applyTheme(data);
      toast.success("Theme saved · entire site updated");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const reset = () => {
    setForm({ ...DEFAULT_THEME });
    applyTheme(DEFAULT_THEME);
  };

  if (loading)
    return <div className="text-sm text-slate-400 font-mono-sms uppercase tracking-[0.2em]">Loading…</div>;

  return (
    <form onSubmit={save} className="max-w-3xl border border-slate-200 p-8" data-testid="theme-settings-form">
      <div className="flex items-center gap-2 mb-6">
        <Palette size={20} weight="duotone" className="text-[var(--sms-primary)]" />
        <div className="font-mono-sms text-xs uppercase tracking-[0.2em]">Site colour theme</div>
      </div>

      {/* Auto-detect from parent website */}
      <div className="mb-8 p-5 bg-slate-50 border border-slate-200">
        <div className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2">
          Auto-detect from parent website
        </div>
        <div className="text-[12px] text-slate-600 mb-3">
          Paste your main website URL — we'll inspect its meta tags, logo and favicon to suggest a brand colour.
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            type="url"
            className="sms-input flex-1"
            placeholder="https://modernskilldevelopment.com"
            value={parentUrl}
            data-testid="theme-parent-url"
            onChange={(e) => setParentUrl(e.target.value)}
          />
          <button
            type="button"
            onClick={detect}
            disabled={detecting}
            data-testid="theme-detect-btn"
            className="sms-btn-secondary inline-flex items-center justify-center gap-2 whitespace-nowrap"
          >
            <MagicWand size={14} weight="bold" />
            {detecting ? "Detecting…" : "Detect colour"}
          </button>
        </div>
        {detectedSource && (
          <div className="mt-2 text-[11px] text-slate-500 truncate">
            Source: <span className="font-mono-sms">{detectedSource}</span>
          </div>
        )}

        {swatches.length > 0 && (
          <div className="mt-4">
            <div className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2">
              Detected palette · click any to use as primary
            </div>
            <div className="flex flex-wrap items-stretch gap-2" data-testid="theme-swatches">
              {swatches.map((s, i) => {
                const active = (form.primary || "").toUpperCase() === s.hex.toUpperCase();
                return (
                  <button
                    key={s.hex + i}
                    type="button"
                    onClick={() => pickSwatch(s.hex)}
                    data-testid={`theme-swatch-${i}`}
                    title={`${s.hex}${s.tag ? " · " + s.tag : ""}`}
                    className={`group relative w-16 h-16 border transition-all ${
                      active ? "border-slate-900 ring-2 ring-offset-2 ring-slate-900" : "border-slate-300 hover:border-slate-900"
                    }`}
                    style={{ background: s.hex }}
                  >
                    {s.tag === "meta" && (
                      <span className="absolute top-0 left-0 bg-slate-900 text-white font-mono-sms text-[8px] uppercase tracking-[0.1em] px-1">
                        meta
                      </span>
                    )}
                    <span className="absolute bottom-0 inset-x-0 bg-white/90 text-[9px] font-mono-sms text-slate-900 py-0.5">
                      {s.hex}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Manual pickers */}
      <div className="grid sm:grid-cols-2 gap-6">
        <ColorField label="Primary" hint="Buttons, links, focus rings, icons." value={form.primary} testid="theme-primary" onChange={(v) => setForm({ ...form, primary: v })} />
        <ColorField label="Primary (hover)" hint="Darker shade for hover / pressed states." value={form.primary_hover} testid="theme-primary-hover" onChange={(v) => setForm({ ...form, primary_hover: v })} />
      </div>

      <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <ColorField label="Accent" hint="Secondary brand colour for highlights." value={form.accent} testid="theme-accent" onChange={(v) => setForm({ ...form, accent: v })} />
        <ColorField label="Success" hint="Verified payments, approvals, ✓ states." value={form.success} testid="theme-success" onChange={(v) => setForm({ ...form, success: v })} />
        <ColorField label="Danger" hint="Errors, rejections, destructive actions." value={form.danger} testid="theme-danger" onChange={(v) => setForm({ ...form, danger: v })} />
        <ColorField label="Warning" hint="Pending approvals, overdue states." value={form.warning} testid="theme-warning" onChange={(v) => setForm({ ...form, warning: v })} />
      </div>

      {/* Live preview */}
      <div className="mt-8 border border-slate-200 p-5">
        <div className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-3">Live preview</div>
        <div className="flex flex-wrap items-center gap-3">
          <button type="button" className="sms-btn-primary">Primary button</button>
          <button type="button" className="sms-btn-secondary">Secondary</button>
          <button type="button" className="sms-btn-danger">Danger</button>
          <span className="font-mono-sms text-xs uppercase tracking-[0.2em] text-[var(--sms-primary)]">Primary text</span>
          <span className="font-mono-sms text-xs uppercase tracking-[0.2em] text-[var(--sms-accent)]">Accent text</span>
          <span className="font-mono-sms text-xs uppercase tracking-[0.2em] text-[var(--sms-success)]">✓ Success</span>
          <span className="font-mono-sms text-xs uppercase tracking-[0.2em] text-[var(--sms-warning)]">⏳ Warning</span>
          <span className="font-mono-sms text-xs uppercase tracking-[0.2em] text-[var(--sms-danger)]">✗ Danger</span>
          <input className="sms-input max-w-xs" placeholder="Focus me to see ring" />
        </div>
      </div>

      <div className="flex items-center gap-3 pt-6 mt-6 border-t border-slate-200">
        <button type="submit" disabled={saving} data-testid="theme-save" className="sms-btn-primary inline-flex items-center gap-2">
          <FloppyDisk size={14} weight="bold" />
          {saving ? "Saving…" : "Save theme"}
        </button>
        <button type="button" onClick={reset} data-testid="theme-reset" className="sms-btn-secondary">
          Reset to default
        </button>
        <div className="text-[11px] text-slate-500 inline-flex items-center gap-1">
          <Info size={12} weight="duotone" /> Theme applies instantly to every page on the site.
        </div>
      </div>
    </form>
  );
}

function ColorField({ label, hint, value, onChange, testid }) {
  const safe = /^#?[0-9a-fA-F]{6}$/.test((value || "").replace("#", "")) ? value : "#000000";
  return (
    <div>
      <div className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2">{label}</div>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={safe}
          onChange={(e) => onChange(e.target.value.toUpperCase())}
          data-testid={`${testid}-picker`}
          className="h-11 w-14 border border-slate-300 cursor-pointer p-0"
        />
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          data-testid={`${testid}-hex`}
          className="sms-input font-mono-sms uppercase flex-1"
          placeholder="#0055FF"
          maxLength={7}
        />
      </div>
      {hint && <div className="text-[11px] text-slate-400 mt-1">{hint}</div>}
    </div>
  );
}

// ----------------------------- Notifications -----------------------------
function NotificationsForm() {
  const [form, setForm] = useState(null);
  const [log, setLog] = useState([]);
  const [saving, setSaving] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [testing, setTesting] = useState(false);
  const [testPhone, setTestPhone] = useState("");
  const [testingWa, setTestingWa] = useState(false);
  const [testPhoneSms, setTestPhoneSms] = useState("");
  const [testingSms, setTestingSms] = useState(false);
  const [providerReady, setProviderReady] = useState(false);
  const [senderEmail, setSenderEmail] = useState("");
  const [waProviderReady, setWaProviderReady] = useState(false);
  const [waFrom, setWaFrom] = useState("");
  const [smsProviderReady, setSmsProviderReady] = useState(false);
  const [smsFrom, setSmsFrom] = useState("");

  const loadLog = () => {
    api.get("/settings/notifications/log?limit=25").then((r) => setLog(r.data)).catch(() => setLog([]));
  };

  useEffect(() => {
    api.get("/settings/notifications").then((r) => {
      const { _provider_ready, _sender_email, _whatsapp_provider_ready, _whatsapp_from, _sms_provider_ready, _sms_from, ...rest } = r.data;
      setForm(rest);
      setProviderReady(!!_provider_ready);
      setSenderEmail(_sender_email || "");
      setWaProviderReady(!!_whatsapp_provider_ready);
      setWaFrom(_whatsapp_from || "");
      setSmsProviderReady(!!_sms_provider_ready);
      setSmsFrom(_sms_from || "");
    });
    loadLog();
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/settings/notifications", form);
      toast.success("Notification settings saved");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    if (!testTo.includes("@")) {
      toast.error("Enter a valid recipient email");
      return;
    }
    setTesting(true);
    try {
      const { data } = await api.post("/settings/notifications/test", { to: testTo });
      if (data.mode === "mocked") {
        toast.success(`Test queued (MOCKED — no key set). See the log below.`);
      } else {
        toast.success(`Test email sent to ${data.to} via live Resend.`);
      }
      loadLog();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || err.message);
    } finally {
      setTesting(false);
    }
  };

  const sendTestSms = async () => {
    const digits = (testPhoneSms.match(/\d/g) || []).length;
    if (digits < 10) {
      toast.error("Enter a phone number with country code (e.g. +919876543210)");
      return;
    }
    setTestingSms(true);
    try {
      const { data } = await api.post("/settings/notifications/test-sms", { to: testPhoneSms });
      if (data.mode === "mocked") {
        toast.success(`SMS test queued (MOCKED — Twilio creds / SMS number missing). See the log below.`);
      } else {
        toast.success(`SMS test sent to ${data.to} via live Twilio.`);
      }
      loadLog();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || err.message);
    } finally {
      setTestingSms(false);
    }
  };

  const sendTestWhatsapp = async () => {
    const digits = (testPhone.match(/\d/g) || []).length;
    if (digits < 10) {
      toast.error("Enter a phone number with country code (e.g. +919876543210)");
      return;
    }
    setTestingWa(true);
    try {
      const { data } = await api.post("/settings/notifications/test-whatsapp", { to: testPhone });
      if (data.mode === "mocked") {
        toast.success(`WhatsApp test queued (MOCKED — Twilio creds missing). See the log below.`);
      } else {
        toast.success(`WhatsApp test sent to ${data.to} via live Twilio.`);
      }
      loadLog();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || err.message);
    } finally {
      setTestingWa(false);
    }
  };

  if (!form) {
    return <div className="text-sm text-slate-400 font-mono-sms uppercase tracking-[0.2em]">Loading…</div>;
  }

  return (
    <form onSubmit={save} className="max-w-4xl border border-slate-200 p-8 space-y-8" data-testid="notifications-form">
      <div className={`p-5 border flex items-start justify-between gap-3 ${providerReady ? "border-[var(--sms-success)] bg-emerald-50" : "border-[var(--sms-warning)] bg-amber-50"}`}>
        <div className="flex items-start gap-3">
          {providerReady ? (
            <CheckCircle size={20} weight="duotone" className="text-[var(--sms-success)] shrink-0 mt-0.5" />
          ) : (
            <Warning size={20} weight="duotone" className="text-[var(--sms-warning)] shrink-0 mt-0.5" />
          )}
          <div>
            <div className="font-mono-sms text-[10px] uppercase tracking-[0.2em] mb-1">
              {providerReady ? "Provider: Resend — LIVE" : "Provider: Resend — MOCKED MODE"}
            </div>
            <div className="text-[12px] text-slate-700">
              {providerReady ? (
                <>Emails will be delivered for real. Sender: <span className="font-mono-sms">{senderEmail}</span></>
              ) : (
                <>No <span className="font-mono-sms">RESEND_API_KEY</span> set. Every send is logged but nothing leaves the server — flip to live by adding the key to <span className="font-mono-sms">backend/.env</span> and restarting.</>
              )}
            </div>
          </div>
        </div>
      </div>

      <div>
        <div className="font-mono-sms text-xs uppercase tracking-[0.2em] mb-4 flex items-center gap-2">
          <EnvelopeSimple size={14} weight="duotone" className="text-[var(--sms-primary)]" />
          When should we send?
        </div>
        <div className="space-y-3">
          <Toggle label="Master switch — Email enabled" value={form.email_enabled} testid="toggle-email-enabled" onChange={(v) => setForm({ ...form, email_enabled: v })} />
          <Toggle label="Full payment verified (student enrolled)" value={form.send_on_enroll} testid="toggle-on-enroll" onChange={(v) => setForm({ ...form, send_on_enroll: v })} />
          <Toggle label="Per-instalment verified" value={form.send_on_installment_verify} testid="toggle-on-installment" onChange={(v) => setForm({ ...form, send_on_installment_verify: v })} />
          <Toggle label="UTR claim received (before verification — sets applicant expectations)" value={form.send_on_payment_claim} testid="toggle-on-claim" onChange={(v) => setForm({ ...form, send_on_payment_claim: v })} />
        </div>
      </div>

      <TemplateEditor title="Welcome / Enrolled email" subjectKey="subject_enroll" bodyKey="body_enroll" form={form} setForm={setForm} />
      <TemplateEditor title="Instalment verified email" subjectKey="subject_installment" bodyKey="body_installment" form={form} setForm={setForm} />
      <TemplateEditor title="UTR received email" subjectKey="subject_claim" bodyKey="body_claim" form={form} setForm={setForm} />

      {/* WhatsApp section */}
      <div className={`p-5 border flex items-start justify-between gap-3 ${waProviderReady ? "border-[var(--sms-success)] bg-emerald-50" : "border-[var(--sms-warning)] bg-amber-50"}`} data-testid="whatsapp-status-banner">
        <div className="flex items-start gap-3">
          {waProviderReady ? (
            <CheckCircle size={20} weight="duotone" className="text-[var(--sms-success)] shrink-0 mt-0.5" />
          ) : (
            <Warning size={20} weight="duotone" className="text-[var(--sms-warning)] shrink-0 mt-0.5" />
          )}
          <div>
            <div className="font-mono-sms text-[10px] uppercase tracking-[0.2em] mb-1">
              {waProviderReady ? "Provider: Twilio WhatsApp — LIVE" : "Provider: Twilio WhatsApp Sandbox — MOCKED MODE"}
            </div>
            <div className="text-[12px] text-slate-700">
              {waProviderReady ? (
                <>WhatsApp will be delivered for real. Sender: <span className="font-mono-sms">{waFrom}</span></>
              ) : (
                <>No <span className="font-mono-sms">TWILIO_ACCOUNT_SID</span> / <span className="font-mono-sms">TWILIO_AUTH_TOKEN</span> set. Every send is logged but nothing leaves the server — add these to <span className="font-mono-sms">backend/.env</span> and restart to go live. Sandbox testers must first send the join code from their WhatsApp to <span className="font-mono-sms">{waFrom}</span>.</>
              )}
            </div>
          </div>
        </div>
      </div>

      <div>
        <div className="font-mono-sms text-xs uppercase tracking-[0.2em] mb-4 flex items-center gap-2">
          <ChatCircleText size={14} weight="duotone" className="text-[var(--sms-primary)]" />
          WhatsApp triggers
        </div>
        <div className="space-y-3">
          <Toggle label="Master switch — WhatsApp enabled" value={form.whatsapp_enabled} testid="toggle-whatsapp-enabled" onChange={(v) => setForm({ ...form, whatsapp_enabled: v })} />
          <div className="text-[11px] text-slate-500 pl-12">
            Uses the same per-event toggles as email above (enrol / instalment / UTR claim). When both channels are on, each triggers in parallel.
          </div>
        </div>
      </div>

      <TemplateEditor
        title="WhatsApp · Welcome / Enrolled"
        bodyKey="wa_text_enroll"
        form={form}
        setForm={setForm}
        plain
      />
      <TemplateEditor
        title="WhatsApp · Instalment verified"
        bodyKey="wa_text_installment"
        form={form}
        setForm={setForm}
        plain
      />
      <TemplateEditor
        title="WhatsApp · UTR received"
        bodyKey="wa_text_claim"
        form={form}
        setForm={setForm}
        plain
      />
      <TemplateEditor
        title="WhatsApp · Instalment overdue"
        bodyKey="wa_text_overdue"
        form={form}
        setForm={setForm}
        plain
      />

      {/* SMS section */}
      <div className={`p-5 border flex items-start justify-between gap-3 ${smsProviderReady ? "border-[var(--sms-success)] bg-emerald-50" : "border-[var(--sms-warning)] bg-amber-50"}`} data-testid="sms-status-banner">
        <div className="flex items-start gap-3">
          {smsProviderReady ? (
            <CheckCircle size={20} weight="duotone" className="text-[var(--sms-success)] shrink-0 mt-0.5" />
          ) : (
            <Warning size={20} weight="duotone" className="text-[var(--sms-warning)] shrink-0 mt-0.5" />
          )}
          <div>
            <div className="font-mono-sms text-[10px] uppercase tracking-[0.2em] mb-1">
              {smsProviderReady ? "Provider: Twilio SMS — LIVE" : "Provider: Twilio SMS — MOCKED MODE"}
            </div>
            <div className="text-[12px] text-slate-700">
              {smsProviderReady ? (
                <>SMS will be delivered for real. Sender: <span className="font-mono-sms">{smsFrom}</span></>
              ) : (
                <>SMS needs <span className="font-mono-sms">TWILIO_ACCOUNT_SID</span>, <span className="font-mono-sms">TWILIO_AUTH_TOKEN</span>, and <span className="font-mono-sms">TWILIO_SMS_FROM</span> (your Twilio phone number). Without these, every send is logged but nothing leaves the server. See <code>TWILIO_SETUP.md</code> for setup.</>
              )}
            </div>
          </div>
        </div>
      </div>

      <div>
        <div className="font-mono-sms text-xs uppercase tracking-[0.2em] mb-4 flex items-center gap-2">
          <ChatCircleText size={14} weight="duotone" className="text-[var(--sms-primary)]" />
          SMS triggers
        </div>
        <div className="space-y-3">
          <Toggle label="Master switch — SMS enabled" value={form.sms_enabled} testid="toggle-sms-enabled" onChange={(v) => setForm({ ...form, sms_enabled: v })} />
          <Toggle label="Send on instalment overdue (cron sweep)" value={form.send_on_overdue} testid="toggle-send-on-overdue" onChange={(v) => setForm({ ...form, send_on_overdue: v })} />
          <div className="text-[11px] text-slate-500 pl-12">
            Overdue nudge fires from <code>POST /api/settings/notifications/payment-overdue-sweep</code> — wire that to your cron (once per day). It's de-duped so each instalment gets at most one nudge per day.
          </div>
        </div>
      </div>

      <TemplateEditor title="SMS · Welcome / Enrolled" bodyKey="sms_text_enroll" form={form} setForm={setForm} plain />
      <TemplateEditor title="SMS · Instalment verified" bodyKey="sms_text_installment" form={form} setForm={setForm} plain />
      <TemplateEditor title="SMS · UTR received" bodyKey="sms_text_claim" form={form} setForm={setForm} plain />
      <TemplateEditor title="SMS · Instalment overdue" bodyKey="sms_text_overdue" form={form} setForm={setForm} plain />

      {/* Email overdue template */}
      <TemplateEditor title="Email · Instalment overdue" subjectKey="subject_overdue" bodyKey="body_overdue" form={form} setForm={setForm} />

      <div className="flex items-center gap-3 pt-6 border-t border-slate-200">
        <button type="submit" disabled={saving} data-testid="notifications-save" className="sms-btn-primary inline-flex items-center gap-2">
          <FloppyDisk size={14} weight="bold" />
          {saving ? "Saving…" : "Save settings"}
        </button>
        <div className="text-[11px] text-slate-500 inline-flex items-center gap-1">
          <Info size={12} weight="duotone" /> Placeholders: {"{{student_name}} {{student_id}} {{course_name}} {{amount_inr}} {{utr}} {{institute_name}}"}
        </div>
      </div>

      <div className="border border-slate-200 p-5 space-y-3">
        <div className="font-mono-sms text-xs uppercase tracking-[0.2em]">Send test email</div>
        <div className="flex flex-col sm:flex-row gap-2">
          <input type="email" className="sms-input flex-1" placeholder="your-email@example.com" value={testTo} onChange={(e) => setTestTo(e.target.value)} data-testid="test-to-input" />
          <button type="button" onClick={sendTest} disabled={testing} data-testid="test-send-btn" className="sms-btn-secondary inline-flex items-center justify-center gap-2 whitespace-nowrap">
            <PaperPlaneTilt size={14} weight="bold" />
            {testing ? "Sending…" : "Send test"}
          </button>
        </div>
      </div>

      <div className="border border-slate-200 p-5 space-y-3" data-testid="whatsapp-test-panel">
        <div className="font-mono-sms text-xs uppercase tracking-[0.2em]">Send test WhatsApp</div>
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            type="tel"
            className="sms-input flex-1 font-mono-sms"
            placeholder="+919876543210"
            value={testPhone}
            onChange={(e) => setTestPhone(e.target.value)}
            data-testid="test-whatsapp-to"
          />
          <button
            type="button"
            onClick={sendTestWhatsapp}
            disabled={testingWa}
            data-testid="test-whatsapp-btn"
            className="sms-btn-secondary inline-flex items-center justify-center gap-2 whitespace-nowrap"
          >
            <PaperPlaneTilt size={14} weight="bold" />
            {testingWa ? "Sending…" : "Send WhatsApp test"}
          </button>
        </div>
        <div className="text-[11px] text-slate-500">
          E.164 format with country code (e.g. <span className="font-mono-sms">+919876543210</span>). In sandbox mode the recipient must have already joined the Twilio sandbox by sending the join code from their WhatsApp.
        </div>
      </div>

      <div className="border border-slate-200 p-5 space-y-3" data-testid="sms-test-panel">
        <div className="font-mono-sms text-xs uppercase tracking-[0.2em]">Send test SMS</div>
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            type="tel"
            className="sms-input flex-1 font-mono-sms"
            placeholder="+919876543210"
            value={testPhoneSms}
            onChange={(e) => setTestPhoneSms(e.target.value)}
            data-testid="test-sms-to"
          />
          <button
            type="button"
            onClick={sendTestSms}
            disabled={testingSms}
            data-testid="test-sms-btn"
            className="sms-btn-secondary inline-flex items-center justify-center gap-2 whitespace-nowrap"
          >
            <PaperPlaneTilt size={14} weight="bold" />
            {testingSms ? "Sending…" : "Send SMS test"}
          </button>
        </div>
        <div className="text-[11px] text-slate-500">
          Needs a Twilio phone number set as <span className="font-mono-sms">TWILIO_SMS_FROM</span>. For Indian recipients, register your business + template under DLT first to avoid carrier blocks.
        </div>
      </div>

      <div className="border border-slate-200">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <div className="font-mono-sms text-xs uppercase tracking-[0.2em]">Recent notifications</div>
          <button type="button" onClick={loadLog} className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-slate-500 hover:text-slate-900">Refresh</button>
        </div>
        {log.length === 0 ? (
          <div className="px-5 py-6 text-sm text-slate-400 font-mono-sms uppercase tracking-[0.15em]" data-testid="notifications-log-empty">No notifications yet</div>
        ) : (
          <div className="divide-y divide-slate-100" data-testid="notifications-log">
            {log.map((n) => (
              <div key={n.id} className="px-5 py-3 flex items-start gap-4 text-sm">
                <StatusPill status={n.status} />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-slate-900 truncate">
                    {n.channel === "whatsapp" ? (n.body || "").slice(0, 80) : n.subject}
                  </div>
                  <div className="font-mono-sms text-[10px] uppercase tracking-[0.15em] text-slate-500 mt-0.5">
                    {(n.channel || "email")} · {n.event} · {n.to || "—"} · {new Date(n.created_at).toLocaleString()}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </form>
  );
}

function Toggle({ label, value, onChange, testid }) {
  return (
    <label className="flex items-center gap-3 cursor-pointer select-none" data-testid={testid}>
      <span className={`relative inline-block w-9 h-5 transition-colors ${value ? "bg-[var(--sms-primary)]" : "bg-slate-300"}`}>
        <span className={`absolute top-0.5 w-4 h-4 bg-white transition-all ${value ? "left-[18px]" : "left-0.5"}`} />
      </span>
      <input type="checkbox" className="hidden" checked={value} onChange={(e) => onChange(e.target.checked)} />
      <span className="text-sm text-slate-700">{label}</span>
    </label>
  );
}

function TemplateEditor({ title, subjectKey, bodyKey, form, setForm, plain = false }) {
  // Sample data per event so the preview reflects what the student will actually see.
  const SAMPLE = {
    student_name: "Priya Sharma",
    student_id: "MSD-2026-042",
    course_name: "Data Science",
    amount_inr: "25,000",
    utr: "AXIS456789012",
    institute_name: "Modern Skill Development",
    installment_number: "2",
    remaining_line: "1 instalment of ₹25,000 remaining.",
    institute_email: "hello@modernskilldevelopment.com",
    institute_phone: "+91 80 1234 5678",
  };
  const render = (tpl) => (tpl || "").replace(
    /\{\{\s*([a-zA-Z_]+)\s*\}\}/g,
    (_, k) => (SAMPLE[k] !== undefined ? SAMPLE[k] : `{{${k}}}`),
  );
  return (
    <div className="border border-slate-100 p-5">
      <div className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-3">{title}</div>
      <div className="grid lg:grid-cols-2 gap-4">
        {/* Editor */}
        <div className="space-y-3">
          {!plain && (
            <input
              className="sms-input w-full"
              value={form[subjectKey] || ""}
              data-testid={`input-${subjectKey}`}
              onChange={(e) => setForm({ ...form, [subjectKey]: e.target.value })}
              placeholder="Subject line"
            />
          )}
          <textarea
            className="sms-input w-full font-mono-sms text-xs"
            rows={plain ? 5 : 8}
            data-testid={`input-${bodyKey}`}
            value={form[bodyKey] || ""}
            onChange={(e) => setForm({ ...form, [bodyKey]: e.target.value })}
            placeholder={plain ? "Plain-text message — WhatsApp does not render HTML" : "HTML body — plain <p>…</p> works great"}
          />
        </div>

        {/* Live preview */}
        <div className="border border-slate-200 bg-slate-50 flex flex-col">
          <div className="px-4 py-2 border-b border-slate-200 bg-white flex items-center justify-between">
            <div className="font-mono-sms text-[9px] uppercase tracking-[0.2em] text-slate-400">Preview · sample data</div>
            <span className="font-mono-sms text-[9px] uppercase tracking-[0.2em] text-[var(--sms-success)]">live</span>
          </div>
          {!plain && (
            <div className="px-4 py-3 border-b border-slate-200 bg-white">
              <div className="font-mono-sms text-[9px] uppercase tracking-[0.15em] text-slate-400 mb-1">Subject</div>
              <div className="text-sm font-medium text-slate-900" data-testid={`preview-subject-${subjectKey}`}>
                {render(form[subjectKey]) || <span className="text-slate-400">—</span>}
              </div>
            </div>
          )}
          {plain ? (
            <pre
              className="px-4 py-3 text-sm text-slate-800 whitespace-pre-wrap font-sans flex-1"
              data-testid={`preview-body-${bodyKey}`}
            >
              {render(form[bodyKey]) || "—"}
            </pre>
          ) : (
            <div
              className="px-4 py-3 text-sm text-slate-800 prose prose-sm max-w-none flex-1"
              data-testid={`preview-body-${bodyKey}`}
              dangerouslySetInnerHTML={{ __html: render(form[bodyKey]) || "<p class='text-slate-400'>—</p>" }}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  const cls = status === "sent" ? "text-[var(--sms-success)]"
    : status === "failed" ? "text-[var(--sms-danger)]"
    : status === "mocked" ? "text-slate-500"
    : "text-[var(--sms-warning)]";
  return (
    <span className={`font-mono-sms text-[10px] uppercase tracking-[0.2em] shrink-0 min-w-[55px] ${cls}`}>
      {status}
    </span>
  );
}

